import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface EvidenceEmptyStateProps {
  /** Current register scope, so the message can say why the list is empty. */
  activeOnly: boolean
  /** Whether any search/status/type filter is applied. */
  hasFilters: boolean
  /** Whole-register total, for pointing the user at the wider scope. */
  registeredTotal: number
}

/**
 * Empty state for the evidence register (FR10).
 *
 * There are three distinct reasons the table can be empty and they call for
 * different actions. A single generic message left the analyst unable to tell
 * "there is nothing here" from "your filters are hiding it" — the difference
 * between moving on and losing an hour.
 */
export function EvidenceEmptyState({
  activeOnly,
  hasFilters,
  registeredTotal,
}: EvidenceEmptyStateProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  let heading: string
  let detail: string

  if (hasFilters) {
    heading = t('empty.noMatches')
    detail = t('empty.noMatchesDescription')
  } else if (activeOnly && registeredTotal > 0) {
    // Narrowed scope is hiding real rows — name the count so the wider view is
    // an obvious next step rather than a guess.
    heading = t('empty.noneInCustodyFlow')
    detail = t('empty.noneInCustodyFlowDescription', { count: registeredTotal })
  } else {
    heading = t('empty.noEvidence')
    detail = t('empty.noEvidenceDescription')
  }

  return (
    <div className="flex h-48 flex-col items-center justify-center gap-2">
      <FileText className="h-8 w-8 text-[var(--text-muted)]" />
      <p className="text-sm text-[var(--text-muted)]">{heading}</p>
      <p className="max-w-md text-center text-xs text-[var(--text-muted)]">{detail}</p>
    </div>
  )
}
